import { useEffect } from 'react';
import { log } from 'log';
import { pick, prop } from 'ramda';
import { useDispatch } from 'react-redux';
import { isExternalUrl } from 'js/utils/url.util';
import { htcmdService } from 'js/services/htcmd.service';
import { useNavigate } from 'js/hooks/navigate/useNavigate';
import { useLoginPopup } from 'js/components/popups/LoginPopup';
import { useAppSelector, useUserSelector, useSportsbookSelector } from 'js/store/hooks';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { setPopupDynamicPagePath } from 'js/components/system/Page/store/actions';

import * as actions from '../store/actions';
import { useSportsbookGetTemptoken } from './useSportsbookGetTemptoken';

export const useSportsbookListeners = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const showLoginPopup = useLoginPopup();
    const {
        depositPage,
        loginPage
    } = useAppSelector(pick(['depositPage', 'loginPage']));
    const sportsbookGetTemptoken = useSportsbookGetTemptoken();
    const isAppInitialized = useSportsbookSelector(prop('isAppInitialized'));
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const sportsbookRootURL = useAppSelector(prop('sportsbookRootURL'));

    useEffect(() => {
        if (!isAppInitialized) {
            return;
        }

        try {
            window.Sportsbook.setListener('SHOW_LOGIN', (...props) => {
                if (isLoggedIn) {
                    window.Sportsbook.sendMessage('SYNC_SESSION');
                } else if (htcmdService.isEnabled) {
                    htcmdService.openLogin();
                } else {
                    if (loginPage) {
                        navigate(loginPage);
                    } else {
                        showLoginPopup(props);
                    }
                }
            });
            window.Sportsbook.setListener('INTERNAL_ROUTE', (route) => dispatch(setPopupDynamicPagePath(route || sportsbookRootURL)));
            window.Sportsbook.setListener('BALANCE_UPDATED', (balance) => dispatch(actions.updateBalances(balance)));
            window.Sportsbook.setListener('GET_SESSION_INFO', sportsbookGetTemptoken);
            window.Sportsbook.setListener('ANALYTICS_EVENT', (event) => publicEventsAPIService.trigger('sportsbook-analytics', event));
        } catch (error) {
            log('log', '[SPORTSBOOK | Portal] Set listeners error', error);
        }
    }, [
        dispatch,
        isAppInitialized,
        showLoginPopup,
        sportsbookRootURL,
        sportsbookGetTemptoken,
        isLoggedIn,
        navigate,
        loginPage
    ]);

    useEffect(() => {
        if (!isAppInitialized) {
            return;
        }

        try {
            window.Sportsbook.setListener('SHOW_DEPOSIT', () => {
                if (htcmdService.isEnabled) {
                    htcmdService.openDeposit();
                } else {
                    navigate(depositPage);
                }
            });
        } catch (error) {
            log('log', '[SPORTSBOOK | Portal] Set listeners error', error);
        }
    }, [depositPage, isAppInitialized, navigate]);

    useEffect(() => {
        if (!isAppInitialized) {
            return;
        }

        try {
            window.Sportsbook.setListener('NAVIGATE', (url, options) => {
                if (url.startsWith('native://')) {
                    htcmdService.sendMessage(url.substr(9), options);

                    return;
                }

                if (isExternalUrl(url) && htcmdService.isEnabled) {
                    htcmdService.openExternalPage(url);

                    return;
                }

                navigate(url);
            });
        } catch (error) {
            log('log', '[SPORTSBOOK | Portal] Set listeners error', error);
        }
    }, [isAppInitialized, navigate]);
};
